import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Post, TreeNode} from "../../../../types/Post";
import ReactQuill from "react-quill";
import {ImageUploader} from "../../../../components/ImageUploader";
import {CategoryTree} from "../CategoryTree";
import {buildBreadcrumbFromTree} from "../../../../utils/category";
import {ActionButton} from "../../../../designSystem/components/ActionButton";
import {Typography} from "../../../../designSystem/components/Typography";
import {CustomDialog} from "../../../../components/DialogCustom";

type PostEditorDialogProps = {
  post: Post | null;
  categories: TreeNode[];
  onCancel: () => void;
  onDelete: () => void;
  onSave: (p: Post) => void;
  uploadFile: (file: File, folder: string) => Promise<string | null>;
  uploadFolder?: string; // 預設 'posts'
  maxImageMB?: number;   // 預設 3MB
};

export const PostEditorDialog = ({
  post,
  categories,
  onCancel,
  onDelete,
  onSave,
  uploadFile,
  uploadFolder = 'posts',
  maxImageMB = 3,
}: PostEditorDialogProps) => {
  const [title, setTitle] = useState<string>(post?.title ?? '');
  const [html, setHtml] = useState<string>(post?.content ?? '');
  const [selectedCategory, setSelectedCategory] = useState<TreeNode | null>(null);
  const [coverUrl, setCoverUrl] = useState<string>(post?.coverImageUrl ?? '');
  const quillRef = useRef<ReactQuill | null>(null);

  useEffect(() => {
    setTitle(post?.title ?? '');
    setHtml(post?.content ?? '');

    if (post) {
      const breadcrumb = buildBreadcrumbFromTree(categories, post.detailCategoryId);
      if (breadcrumb.length > 0) {
        setSelectedCategory(breadcrumb[breadcrumb.length - 1]);
      }
    }
  }, [post]);

  const handleSave = () => {
    onSave({
      ...post,
      title,
      content: html,
      detailCategoryId: selectedCategory.id,
      coverImageUrl: coverUrl
    })
  }

  // 自訂上傳圖片 handler：開檔→檢查大小→上傳→插入 <img src="...">
  const handleImage = useCallback(async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const limit = maxImageMB * 1024 * 1024;
      if (file.size > limit) {
        alert(`圖片超過 ${maxImageMB}MB，請重新選擇`);
        return;
      }

      // 上傳
      const url = await uploadFile(file, uploadFolder);
      if (!url) {
        alert('圖片上傳失敗，請稍後再試');
        return;
      }

      // 插入到 Quill
      const editor = quillRef.current?.getEditor();
      const range = editor?.getSelection(true);
      if (editor && range) {
        editor.insertEmbed(range.index, 'image', url, 'user');
        editor.setSelection(range.index + 1, 0, 'user');
      }
    };

    input.click();
  }, [uploadFile, uploadFolder, maxImageMB]);

  const handleCoverImageUploaded = (url: string) => {
    setCoverUrl(url)
  }

  // Quill 模組：工具列 + 自訂 image handler
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['blockquote', 'code-block'],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: {
          image: handleImage,
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    [handleImage]
  );

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'blockquote', 'code-block',
    'link', 'image',
    'clean',
  ];

  const canSubmit = title && html && selectedCategory;

  return (
    <CustomDialog>
      <div className="flex flex-col gap-4 text-left">
        <div className="flex flex-row justify-between items-center">
          <Typography variant="title">
            衛教文章編輯
          </Typography>
          <div className="flex gap-2">
            <ActionButton
              label="取消"
              onClick={onCancel}
              color="secondary"
            />
            { post
              ? (
                <ActionButton
                  label="永久刪除"
                  iconName="fa-trash"
                  onClick={onDelete}
                  color="warning"
                  variant="outline"
                />
              ): null
            }
            <ActionButton
              label="儲存"
              iconName="fa-save"
              onClick={handleSave}
              disabled={!canSubmit}
            />
          </div>
        </div>

        <div className="flex flex-1 gap-x-3">
          {/* Left */}
          <div className="flex flex-2 flex-col gap-y-3">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full h-12 border border-gray-300 rounded-md px-3 outline-none focus:ring-2 focus:ring-blue-200"
              placeholder="輸入標題"
            />
            <div className="[&_ .ql-container]:max-h-[100px]">
              <ReactQuill
                ref={(el) => (quillRef.current = el)}
                theme="snow"
                value={html}
                onChange={setHtml}
                modules={modules}
                formats={formats}
                placeholder="在此輸入內容，支援粗體、顏色、圖片、連結等…"
                className={'h-[calc(90vh-190px)] w-[600px]'}
              />
            </div>
          </div>

          {/* Right */}
          <div className="flex flex-1 flex-col gap-y-3 h-[calc(90vh-100px)] overflow-y-scroll">
            <div className="flex flex-col gap-y-3">
              <Typography variant="subtitle">
                封面
              </Typography>
              <ImageUploader
                isSquareCropper
                handleUploadedImage={handleCoverImageUploaded}
                uploadFolder="coverImage"
                uploadedImageUrl={coverUrl}
              />
            </div>

            <div className="flex flex-col gap-y-3">
              <Typography variant="subtitle">
                分類
              </Typography>
              <CategoryTree
                editMode={false}
                data={categories}
                selectedId={selectedCategory?.id ?? null}
                onSelect={(n) => setSelectedCategory(n)}
                isNodeSelectable={false}
              />
            </div>
          </div>
        </div>
      </div>
    </CustomDialog>
  );
};
